import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Short-link redirect component. Used for /review, /whatsapp, /book.
 * Redirects instantly on mount with window.location.replace so:
 *   - the browser history doesn't accumulate the short URL
 *   - external links (wa.me / Google) fully take over the tab
 *
 * `target` is either a full URL (https://...) or a function that
 * receives the detected language and returns the target URL.
 *
 * Small "Redirecting..." fallback shown for the 1-2 frames before
 * the browser actually leaves the page. Also serves as a friendly
 * screen if the target is a same-origin path being routed by SPA.
 */

const detectLang = () => {
  const path = window.location.pathname.match(/^\/(pt|en|es|fr)(\/|$)/);
  if (path) return path[1];
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return ['pt', 'en', 'es', 'fr'].includes(nav) ? nav : 'en';
};

export const ShortLinkRedirect = ({ target, label = 'Redirecting…' }) => {
  useEffect(() => {
    const lang = detectLang();
    const url = typeof target === 'function' ? target(lang) : target;

    // If it's a same-origin path (starts with /), use assign so React can pick it up
    // Otherwise use replace so history isn't polluted
    if (url.startsWith('/')) {
      window.location.assign(url);
    } else {
      window.location.replace(url);
    }
  }, [target]);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#0a0a0a] text-white"
      data-testid="short-link-redirect"
    >
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
        <p className="text-lg font-medium tracking-wide">{label}</p>
        <p className="text-xs text-zinc-400">GO VIP Portugal</p>
      </div>
    </div>
  );
};

/**
 * URLs used by short-link redirects. Central place, easy to update.
 */
export const SHORT_LINKS = {
  // WhatsApp — international format, no dashes, no spaces
  whatsapp: (lang) => {
    const messages = {
      pt: 'Olá! Gostaria de reservar um transfer/tour com a GO VIP Portugal.',
      en: 'Hello! I would like to book a transfer/tour with GO VIP Portugal.',
      es: '¡Hola! Me gustaría reservar un traslado/tour con GO VIP Portugal.',
      fr: 'Bonjour ! Je souhaite réserver un transfert/tour avec GO VIP Portugal.',
    };
    return `https://wa.me/351932651940?text=${encodeURIComponent(messages[lang] || messages.en)}`;
  },
  // Google Business Profile — opens GO VIP Portugal directly on Maps
  // (better UX than /writereview which forces sign-in first)
  // CID 8116667297370837121 = hex 0x70a431e72bd19081 (from Google Feature ID)
  review: 'https://maps.google.com/?cid=8116667297370837121',
  // Book — jump to booking form on the localised homepage
  book: (lang) => `/${lang}#booking`,
};
